import { PokerRunEvent } from "@models/PokerRunEvent";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { useManageEventStore } from "@/store/ManageEvent";
import { useUserProfileStore } from "@/store/UserProfile";
import { Ref, ref, ComputedRef, computed } from "vue";
import { EventManager } from "@models/EventManager";
import { Stop } from "@models/Stop";
import Config from "@/config/Config";
import useCodeGenerator from "./useCodeGenerator";
import useUI from "./useUI";

export default function useManageEventHelper(): {
  nextPage: Ref<string>;
  currentEvent: Ref<PokerRunEvent>;
  managers: Ref<EventManager[]>;
  isNew: Ref<boolean>;
  maxStepCompleted: Ref<number>;
  canSave: ComputedRef<boolean>;
  canAssistantSave: ComputedRef<boolean>;
  continueSetup: () => void;
  next: () => void;
  previous: () => void;
  save: () => void;
  cancel: () => void;
  saveAndReturn: () => void;
  cancelSetup: () => void;
  returnToSummary: () => void;
  saveManager: (manager: EventManager) => Promise<EventManager>;
  deleteManager: (managerId: string) => Promise<void>;
  loadManagers: () => Promise<EventManager[]>;
  isUSEvent: Ref<boolean>;
  shareMangerLink: (manager: EventManager, generateNewCode: boolean) => void;
  // eslint-disable-next-line indent
} {
  const nextPage = ref("");
  const router = useRouter();
  const { isNew, currentEvent, managers, isUSEvent, maxStepCompleted } = storeToRefs(
    useManageEventStore()
  );
  const { loadEvent, setEvent, saveManager, deleteManager, loadManagers, updateMaxStepCompleted } =
    useManageEventStore();
  const { profile, isSupportAdmin } = useUserProfileStore();
  const { generateCode } = useCodeGenerator();

  const canSave = computed(() => {
    return (
      isNew.value ||
      isSupportAdmin ||
      currentEvent.value.organizerId === profile.id ||
      (currentEvent.value.admins !== undefined &&
        currentEvent.value.admins?.some((x) => x === profile.id))
    );
  });

  const canAssistantSave = computed(() => {
    return (
      canSave.value ||
      (currentEvent.value.assistants !== undefined &&
        currentEvent.value.assistants?.some((x) => x === profile.id))
    );
  });

  const next = async () => {
    // Get the dismissKeyboard function from useUI
    const { dismissKeyboard } = useUI();

    // Dismiss keyboard before navigation to prevent layout issues on iOS
    dismissKeyboard();

    const currentPath = router.currentRoute.value.name as string;
    let nextStep = 1;
    let params = {};
    switch (currentPath) {
      case "ManageEventInfo":
        nextPage.value = "ManageEventCard";
        nextStep = 2;
        break;

      case "ManageEventCard":
        nextPage.value = "ManageEventStopEdit";
        params = { order: 1 };
        nextStep = 3;
        break;
      case "ManageEventStopEdit":
        nextPage.value = "ManageEventFees";
        nextStep = 4;
        break;
      case "ManageEventFees":
        nextPage.value = "ManageEventConfirmation";
        nextStep = 4;
        break;
      default:
        nextPage.value = "ManageEventSummary";
        nextStep = 4;
    }

    await updateMaxStepCompleted(nextStep);

    // Add a small delay to ensure keyboard is fully dismissed before navigation
    setTimeout(() => {
      router.push({ name: nextPage.value, params });
    }, 50);
  };

  const previous = async () => {
    // Get the dismissKeyboard function from useUI
    const { dismissKeyboard } = useUI();

    // Dismiss keyboard before navigation to prevent layout issues on iOS
    dismissKeyboard();

    const currentPath = router.currentRoute.value.name as string;
    let nextStep = maxStepCompleted.value;

    switch (currentPath) {
      case "ManageEventCard":
        nextPage.value = "ManageEventInfo";
        nextStep = 1;
        break;

      case "ManageEventStopEdit":
        nextPage.value = "ManageEventCard";
        nextStep = 2;
        break;

      case "ManageEventFees":
        nextPage.value = "ManageEventStopEdit";
        nextStep = 3;
        break;

      default:
        nextPage.value = "ManageEventInfo";
        nextStep = 1;
    }

    await updateMaxStepCompleted(nextStep);

    // Add a small delay to ensure keyboard is fully dismissed before navigation
    setTimeout(() => {
      router.push({ name: nextPage.value });
    }, 50);
  };

  const save = async () => {
    if (isNew.value) {
      currentEvent.value.organizerId = profile.id;
    }

    await setEvent(currentEvent.value);
  };

  const continueSetup = async () => {
    // Get the dismissKeyboard function from useUI
    const { dismissKeyboard } = useUI();

    // Dismiss keyboard before navigation
    dismissKeyboard();

    await save();
    next();
  };

  const cancel = async () => {
    // Get the dismissKeyboard function from useUI
    const { dismissKeyboard } = useUI();

    // Dismiss keyboard before navigation
    dismissKeyboard();

    const currentRoute = router.currentRoute.value;

    // Check if this is part of the setup flow for a new event
    if (currentRoute.meta.isSetupFlow && isNew.value) {
      // For new event setup, cancel the entire setup
      cancelSetup();
      return;
    }

    // Use the cancelRoute from metadata if available, otherwise default to ManageEventSummary
    const cancelRoute = (currentRoute.meta.cancelRoute as string) || "ManageEventSummary";

    // Ensure we're using replace to avoid creating unwanted history entries
    setTimeout(() => {
      router.replace({ name: cancelRoute });
    }, 50);
  };

  const cancelSetup = async () => {
    // Get the dismissKeyboard function from useUI
    const { dismissKeyboard } = useUI();

    // Dismiss keyboard before navigation
    dismissKeyboard();

    setTimeout(() => {
      router.replace({ name: "MyHostedEvents" });
    }, 50);
  };

  const returnToSummary = () => {
    // Get the dismissKeyboard function from useUI
    const { dismissKeyboard } = useUI();

    // Dismiss keyboard before navigation
    dismissKeyboard();

    setTimeout(() => {
      router.replace({ name: "ManageEventSummary" });
    }, 50);
  };

  const saveAndReturn = async () => {
    // Get the dismissKeyboard function from useUI
    const { dismissKeyboard } = useUI();

    // Dismiss keyboard before navigation
    dismissKeyboard();

    await save();

    const currentRoute = router.currentRoute.value;

    // Use the saveRoute from metadata if available, otherwise default to ManageEventSummary
    const saveRoute = (currentRoute.meta.saveRoute as string) || "ManageEventSummary";

    setTimeout(() => {
      router.replace({ name: saveRoute });
    }, 50);
  };

  const shareMangerLink = async (manager: EventManager, generateNewCode: boolean) => {
    if (generateNewCode) {
      manager.code = generateCode(10);
      await saveManager(manager);
    }

    const url = `${Config.LinkUrl}/joinlanding/${currentEvent.value.id}/${manager.code}`;
    const data = {
      url,
      text: `Help me on the ${currentEvent.value?.name} event!`,
      title: `Claim your ${currentEvent.value?.name} event manager invite!`,
    };
    window.navigator.share(data);
  };

  return {
    nextPage,
    currentEvent,
    managers,
    isNew,
    maxStepCompleted,
    canSave,
    canAssistantSave,
    isUSEvent,
    continueSetup,
    next,
    previous,
    save,
    cancel,
    saveAndReturn,
    cancelSetup,
    returnToSummary,
    saveManager,
    deleteManager,
    loadManagers,
    shareMangerLink,
  };
}
